import { BrandPageQueryObj } from "@faire/retailer-shared/search/BrandPageQueryObj";
import { StrictLocalizeFunction } from "@faire/web--source/common/localization";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { Carousel } from "@faire/web--source/slate-labs/Carousel/Carousel";
import { ResponsiveProp } from "@faire/web--source/slate/Theme/ResponsiveProp";
import { ILayoutElement } from "@faire/web-api--source/indigofair/data/ILayoutElement";
import { ILayoutElementData } from "@faire/web-api--source/indigofair/data/ILayoutElementData";
import * as React from "react";

import { IProps as ICarouselProps } from "@faire/retailer/components/Carousel/Carousel";
import { CTATileData } from "@faire/retailer/components/Carousel/CTATile";
import { Context } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Context";
import { ILeanProductWithBrandTile } from "@faire/retailer/lib/ILeanProductWithBrandTile";
import { ProductPagePaths } from "@faire/retailer/paths/ProductPage";

type CarouselCommonProps<T> = Pick<
  ICarouselProps<T>,
  | "renderItem"
  | "peekGapPercent"
  | "onScroll"
  | "onMobileScroll"
  | "peekNext"
  | "items"
  | "padItems"
  | "noWrap"
  | "enableMouseEvents"
  | "lateralPadding"
  | "buttonsColor"
  | "ctaTileData"
  | "className"
  | "buttonsOffsetTop"
  | "swipeableLeftPadding"
  | "peekPadding"
>;

export interface BaseCarouselProps {
  recordAdImpression?: boolean;
  itemsPerSlide?: ResponsiveProp<number>;
  filterItems?: (layoutData: ILayoutElementData) => boolean;
  className?: string;
  itemGap?: ResponsiveProp<number>;
  peekNextPercent?: ResponsiveProp<number>;
  peekNext?: boolean;
  gridView?: boolean;
  noWrap?: boolean;
  brandPageQueryParams?: BrandPageQueryObj;
  productPageQueryParams?: ProductPagePaths.index.IQueries;
  ctaTileData?: CTATileData;
  showBrandMinimum?: boolean;
  lateralPadding?: number;
  useIconArrows?: boolean;
  iconArrowsOffsetTop?: string;
  arrowOffsetX?: number;
  shouldShowDisabledNavigationArrows?: boolean;
  deferInitializationUntilView?: boolean;
  showNavigationArrows?: ResponsiveProp<boolean>;
}

export interface IProps<T> extends BaseCarouselProps, CarouselCommonProps<T> {
  onItemsVisible?: (items: T[]) => void;
}

export const CarouselBase = <T,>(props: IProps<T>) => {
  const {
    arrowOffsetX,
    renderItem,
    items,
    onItemsVisible,
    shouldShowDisabledNavigationArrows,
    deferInitializationUntilView,
    showNavigationArrows,
  } = props;
  const { layoutElement } = React.useContext(Context);

  return (
    <Carousel
      renderItem={renderItem}
      items={items}
      itemsPerSlide={props.itemsPerSlide}
      itemGap={props.itemGap}
      peekNextPercent={props.peekNextPercent}
      dotsVariant={
        layoutElement.background_color &&
        layoutElement.type === "TALL_CATEGORY_TILE"
          ? "secondary"
          : "primary"
      }
      arrowColorOrVariant={layoutElement.main_text_color}
      arrowOffsetX={arrowOffsetX}
      shouldShowDisabledNavigationArrows={shouldShowDisabledNavigationArrows}
      onVisible={onItemsVisible}
      ctaTileData={props.ctaTileData}
      loop={{
        mobileAndAbove: false,
        tabletAndAbove: !props.noWrap,
      }}
      attachRefOnView={deferInitializationUntilView}
      showNavigationArrows={showNavigationArrows}
    />
  );
};

export const getTokensForViewEvent = (
  layoutElement: ILayoutElement,
  strictLocalize: StrictLocalizeFunction
) => {
  switch (layoutElement.type) {
    case ILayoutElement.Type.SMALL_MAKER_TILE_CAROUSEL:
      return layoutElement.layout_element_data.map(
        (layoutElementData) => layoutElementData.brand_tile?.token
      );
    case ILayoutElement.Type.PRODUCT_TILE_CAROUSEL:
    case ILayoutElement.Type.LEAN_PRODUCT_WITH_BRAND_TILE_CAROUSEL:
    case ILayoutElement.Type.VERTICALLY_EXPANDABLE_PRODUCT_GRID:
      return layoutElement.layout_element_data
        .map(
          (layoutElementData) =>
            ILeanProductWithBrandTile.fromLayoutElementData(
              layoutElementData,
              strictLocalize
            )?.product?.product?.token
        )
        .filter(isNotUndefined);
    case ILayoutElement.Type.AVATAR_CAROUSEL:
    case ILayoutElement.Type.AVATAR_CAROUSEL_WITH_GRID:
    case ILayoutElement.Type.TALL_CATEGORY_TILE:
      return layoutElement.layout_element_data.map(
        (layoutElementData) =>
          layoutElementData.category_tile?.category_path_identifier ??
          layoutElementData.simple_image_title?.event_tracking_token
      );
    case ILayoutElement.Type.RECENT_SEARCHES_CAROUSEL:
      return layoutElement.layout_element_data.map((layoutElementData) => {
        const query = layoutElementData.search_query_tile?.query;
        const description = layoutElementData.search_query_tile?.description;
        return description ? `${query}|${description}` : query;
      });
    case ILayoutElement.Type.RETAILER_BRAND_INSIGHTS_CAROUSEL:
      return layoutElement.layout_element_data.map(
        (layoutElementData) => layoutElementData.insight_tile?.insight_token
      );
    default:
      return null;
  }
};
